import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import type { IconName } from '../../element/Icon/icon.types';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { IconButton } from '../../element/Button/IconButton';

const noop = () => {};

export type RawSortableItemProps = {
  value: UniqueIdentifier;
  children: React.ReactNode;
  dismissible: boolean;
  dismissibleIcon?: IconName;
  onRemove?: (id: UniqueIdentifier) => void;
  hideHandle: boolean;
  name: string;
  isHighlighted?: boolean;
  onItemClicked?: () => void;
};

export const RawSortableItem = ({
  value,
  children,
  dismissible,
  dismissibleIcon = 'trash',
  onRemove = noop,
  hideHandle,
  name,
  isHighlighted = false,
  onItemClicked,
}: RawSortableItemProps) => {
  const {
    attributes: dndAttributes,
    index: dndIndex,
    listeners: dndListeners,
    overIndex: dndOverIndex,
    setNodeRef: dndSetNodeRef,
    transform: dndTransform,
    transition: dndTransition,
    isDragging: dndIsDragging,
    isSorting: dndIsSorting,
  } = useSortable({
    id: value,
    data: {
      containerId: name,
      hideHandle,
    },
  });

  // NOTE: Style is required here for handling motion of resorting
  const dndStyle = {
    transform: CSS.Transform.toString(dndTransform),
    transition: dndTransition,
    '--translate-x': dndTransform
      ? `${Math.round(dndTransform.x)}px`
      : undefined,
    '--translate-y': dndTransform
      ? `${Math.round(dndTransform.y)}px`
      : undefined,
    overIndex: dndOverIndex,
    id: value,
    index: dndIndex,
    isDragging: dndIsDragging,
    isSorting: dndIsSorting,
  };

  return (
    <div
      className={AttrsHelper.formatClassname(
        'sortable-item',
        dndIsDragging && 'is-dragging',
        dndIsSorting && 'is-sorting',
        isHighlighted && 'highlighted',
        onItemClicked && 'clickable'
      )}
      data-test={value}
      data-value={value}
      key={value}
      ref={dndSetNodeRef}
      style={dndStyle}
      role={dndAttributes.role}
      aria-describedby={dndAttributes['aria-describedby']}
      aria-disabled={dndAttributes['aria-disabled']}
      aria-pressed={dndAttributes['aria-pressed']}
      aria-roledescription={dndAttributes['aria-roledescription']}
      tabIndex={dndAttributes.tabIndex}
      onClick={
        onItemClicked
          ? () => {
              if (!dndIsDragging) {
                onItemClicked();
              }
            }
          : undefined
      }
    >
      <div className="sortable-item-content">
        <Layout position="middle space-between" horizontalGutter="16px">
          {hideHandle ? null : (
            <span
              {...dndListeners}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <IconButton
                name="sortable-item-handle"
                icon="handle"
                pattern="secondary"
                variation="minimal"
              />
            </span>
          )}
          <LayoutItem fluid>
            <div>{children}</div>
          </LayoutItem>
          {dismissible ? (
            <LayoutItem>
              <IconButton
                name="sortable-item-remove"
                icon={dismissibleIcon}
                variation="minimal"
                pattern="secondary"
                onClick={e => {
                  e.stopPropagation();
                  onRemove(value);
                }}
              />
            </LayoutItem>
          ) : null}
        </Layout>
      </div>
    </div>
  );
};
